// Sentry crash + error reporting for the Founder Ops console.
//
// Deliberately narrow: no performance tracing, no session replay, no
// integrations beyond the browser defaults. This surface is founder-only
// and low-volume, so the goal is "capture uncaught errors and unhandled
// rejections when the founder is using the tool" — not analytics.
//
// Silent when no DSN is set. That keeps `npm run dev` on localhost free
// of Sentry chatter unless you explicitly wire a DSN into your local
// `.env`. On the hosted Cloudflare Pages deploy, set `VITE_SENTRY_DSN` in
// the project's env vars and the SDK activates on the next build.
//
// Sourcemap uploads happen at CI/build time in a separate step (not wired
// here). Runtime keeps only the DSN, which is a public identifier.

import * as Sentry from '@sentry/browser';

const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? '';
// Vite sets MODE to 'development' during `npm run dev` and 'production'
// otherwise. Distinct env label lets us filter dashboard by surface.
const ENV = `ops-console:${import.meta.env.MODE ?? 'production'}`;

let _initialized = false;

/**
 * Initialise the browser Sentry SDK. Safe to call once at module load. A
 * repeat call is a no-op. If VITE_SENTRY_DSN is unset, this returns
 * immediately and no SDK code runs.
 */
export function initClientMonitoring(): void {
  if (_initialized) return;
  _initialized = true;

  if (!DSN) {
    // No DSN — silent in local dev. This is intentional; do not log
    // anything so the console stays clean.
    return;
  }

  Sentry.init({
    dsn:               DSN,
    environment:       ENV,
    // No performance monitoring; the ops console is not latency-sensitive
    // and traces would add noise + bundle size.
    tracesSampleRate:  0,
    // Sample all errors — this surface has one user (the founder) so
    // per-event volume is negligible even at 100%.
    sampleRate:        1.0,
    // Release stamp: git commit or CF Pages build id when present.
    release:           (import.meta.env.VITE_APP_RELEASE as string | undefined) ?? undefined,

    beforeSend(event) {
      try {
        // Defence-in-depth PII scrub. The ops console never touches
        // customer content by design (Founder Ops backend already scrubs
        // before returning), but strip identifying user fields anyway so
        // Sentry stays clean of anything sensitive.
        if (event.user) {
          delete event.user.email;
          delete event.user.username;
          event.user.ip_address = undefined;
        }
        // Never let request payloads / cookies land in Sentry.
        if (event.request) {
          delete event.request.cookies;
          delete event.request.data;
        }
      } catch {
        // beforeSend must never throw — Sentry would drop the event.
      }
      return event;
    },
  });
}

/**
 * Explicit capture for cases where an error is caught + swallowed but
 * still worth reporting. Callers use this from admin-facing error boundaries
 * or non-throwing failure paths (network 5xx, expected-but-notable outcomes).
 */
export function captureError(error: unknown, context?: string): void {
  if (!_initialized || !DSN) return;
  Sentry.withScope((scope) => {
    if (context) scope.setTag('context', context);
    scope.setTag('surface', 'ops-console');
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(String(error), 'error');
    }
  });
}

/** Programmatic status message capture (info / warning / error). */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
  if (!_initialized || !DSN) return;
  Sentry.captureMessage(message, level);
}
